import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Font,
  Image,
} from "@react-pdf/renderer";
import dayjs from "dayjs";

Font.register({
  family: "Manrope",
  fonts: [
    {
      src: "https://fonts.gstatic.com/s/manrope/v15/xn7_tHE41ni1AdIRqXPExKno.ttf",
      fontWeight: "normal",
    },
    {
      src: "https://fonts.gstatic.com/s/manrope/v15/xn7_tHE41ni1AdIRqYPExKno.ttf",
      fontWeight: "bold",
    },
  ],
});

const styles = StyleSheet.create({
  page: {
    paddingTop: 5,
    paddingBottom: 70,
    fontSize: 10,
    fontFamily: "Manrope",
    color: "#333",
  },
  headerSpace: {
    width: "100%",
    height: 80,
    marginBottom: 20,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  header_image: {
    width: "95%",
    height: "100%",
    objectFit: "contain",
  },
  contentWrapper: {
    paddingHorizontal: 30,
  },
  mainTitle: {
    fontSize: 16,
    fontWeight: "bold",
    textAlign: "center",
    textTransform: "uppercase",
    marginBottom: 15,
    borderBottomWidth: 1.5,
    borderBottomColor: "#000",
    paddingBottom: 3,
  },
  infoSection: {
    flexDirection: "row",
    borderWidth: 1,
    borderColor: "#000",
  },
  infoBox: {
    flex: 1,
    borderRightWidth: 1,
    borderRightColor: "#000",
    padding: 6,
  },
  rightBox: {
    width: 120,
    padding: 6,
  },
  label: {
    fontSize: 7,
    color: "#666",
    textTransform: "uppercase",
    marginBottom: 2,
    fontWeight: "bold",
  },
  value: {
    fontSize: 9,
    fontWeight: "bold",
  },
  fullWidthBox: {
    width: "100%",
    borderWidth: 1,
    borderTopWidth: 0,
    borderColor: "#000",
    padding: 6,
    marginBottom: 15,
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#f2f2f2",
    borderWidth: 1,
    borderColor: "#000",
  },
  tableRow: {
    flexDirection: "row",
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderColor: "#000",
    minHeight: 22,
    alignItems: "center",
  },
  cell: { flex: 1, padding: 4, fontSize: 8 },
  cellRight: {
    flex: 0.8,
    padding: 4,
    fontSize: 8,
    textAlign: "right",
    fontWeight: "bold",
  },
  signatureSection: {
    position: "absolute",
    bottom: 40,
    left: 30,
    right: 30,
    flexDirection: "row",
    justifyContent: "space-between",
  },
  signBox: {
    width: "20%",
    borderTopWidth: 1,
    borderTopColor: "#000",
    paddingTop: 5,
    textAlign: "center",
    fontSize: 8,
    fontWeight: "bold",
  },
  footerPageInfo: {
    position: "absolute",
    bottom: 15,
    left: 30,
    right: 30,
    flexDirection: "row",
    justifyContent: "space-between",
    fontSize: 7,
    color: "grey",
  },
});

const PettyCashDocument = ({ pcPrintDataFromAPI }) => {
  const apiPaths = JSON.parse(localStorage.getItem("apiPaths") || "[]");
  const headerImage = apiPaths[0]?.REP_HEADER;

  const data = pcPrintDataFromAPI?.[0] || {};

  // 2. Apply filtering logic (Debit rows where pdc_acc is 'Y')
  const filteredInvoiceList = (pcPrintDataFromAPI || []).filter(
    (inv) =>
      String(inv?.type).trim().toUpperCase() === "D" &&
      String(inv?.pdc_acc).trim().toUpperCase() === "Y",
  );

  const calculatedTotal = filteredInvoiceList.reduce((acc, curr) => {
    return acc + (Number(curr.VAMT) || 0);
  }, 0);

  const formatCurrency = (amt) =>
    Number(amt || 0).toLocaleString(undefined, { minimumFractionDigits: 2 });

  if (!pcPrintDataFromAPI || pcPrintDataFromAPI.length === 0) return null;

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.headerSpace} fixed>
          {headerImage && (
            <Image src={headerImage} style={styles.header_image} />
          )}
        </View>

        <View style={styles.contentWrapper}>
          <Text style={styles.mainTitle}>Petty Cash Voucher</Text>

          {/* Info Section */}
          <View style={styles.infoSection}>
            <View style={styles.infoBox}>
              <Text style={styles.label}>Voucher No</Text>
              <Text style={styles.value}>{data?.TYPE_NO}</Text>
            </View>
            <View style={styles.infoBox}>
              <Text style={styles.label}>Date</Text>
              <Text style={styles.value}>
                {data?.JVDATE ? dayjs(data.JVDATE).format("DD-MM-YYYY") : "-"}
              </Text>
            </View>
            <View style={styles.rightBox}>
              <Text style={styles.label}>Dept</Text>
              <Text style={styles.value}>{data?.DEPTNO || "-"}</Text>
            </View>
          </View>

          <View style={[styles.infoSection, { borderTopWidth: 0 }]}>
            <View style={[styles.infoBox, { flex: 2 }]}>
              <Text style={styles.label}>Paid From / Account Name</Text>
              <Text style={styles.value}>
                {data?.CUST_ACC} - {data?.ACCDESC}
              </Text>
            </View>
            <View style={[styles.rightBox, { backgroundColor: "#fafafa" }]}>
              <Text style={styles.label}>Total Amount</Text>
              <Text style={[styles.value, { fontSize: 11 }]}>
                {/* Use the internally calculated total here */}
                {formatCurrency(calculatedTotal)}
              </Text>
            </View>
          </View>

          <View style={styles.fullWidthBox}>
            <Text style={styles.label}>Description / Remarks</Text>
            <Text style={styles.value}>
              {data?.COMMENTS || data?.entry_ref || "-"}
            </Text>
          </View>

          {/* Table Header */}
          <View style={styles.tableHeader}>
            <Text style={[styles.cell, { fontWeight: "bold" }]}>Ref</Text>
            <Text style={[styles.cell, { fontWeight: "bold" }]}>Date</Text>
            <Text style={[styles.cell, { fontWeight: "bold" }]}>Account</Text>
            <Text style={[styles.cell, { flex: 1.5, fontWeight: "bold" }]}>
              Description
            </Text>
            <Text style={[styles.cellRight, { fontWeight: "bold" }]}>
              Allocated Amt
            </Text>
          </View>

          {/* Filtered Table Data */}
          {filteredInvoiceList.map((inv, idx) => (
            <View style={styles.tableRow} key={idx}>
              <Text style={styles.cell}>{inv.ref?.trim() || "-"}</Text>
              <Text style={styles.cell}>
                {inv.JVDATE ? dayjs(inv.JVDATE).format("DD-MM-YYYY") : "-"}
              </Text>
              <Text style={styles.cell}>{inv.account || "-"}</Text>
              <Text style={[styles.cell, { flex: 1.5 }]}>{inv.entry_ref}</Text>
              <Text style={styles.cellRight}>{formatCurrency(inv.VAMT)}</Text>
            </View>
          ))}
        </View>

        {/* Static Bottom Sections */}
        <View style={styles.signatureSection} fixed>
          <Text style={styles.signBox}>Prepared By</Text>
          <Text style={styles.signBox}>Verified By</Text>
          <Text style={styles.signBox}>Approved By</Text>
          <Text style={styles.signBox}>Received By</Text>
        </View>

        <View style={styles.footerPageInfo} fixed>
          <Text>
            {dayjs().format("DD-MM-YYYY h:mm A")} | User:{" "}
            {data?.user_id || "System"}
          </Text>
          <Text
            render={({ pageNumber, totalPages }) =>
              `Page ${pageNumber} of ${totalPages}`
            }
          />
        </View>
      </Page>
    </Document>
  );
};

export default PettyCashDocument;
